import React, {useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import ColorButton from "../../../../components/color-button/color-button.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import PriorityService from "../../../../services/dispatch/priority-service.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialPriority = {name: "", timer: "00:00", color: "#000000", priorityDefault: false};

const timerToMinutes = (timer) => (timer ?? "00:00")
    .split(":")
    .reduce((total, time, index) => total + (index === 0 ? Number(time) * 60 : Number(time)), 0);

const PriorityForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialPriority);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);

    const {isLoading: isQueryLoading, refetch} = useQuery({
        queryFn: async () => {
            const data = await PriorityService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const {priority} = data;
            setState({
                id: priority.id,
                name: priority.name ?? "",
                timer: priority.timer ?? "00:00",
                color: priority.color ?? "#000000",
                priorityDefault: !!priority.priorityDefault,
            });
            return priority;
        },
        queryKey: ["dispatch-priority", id],
        enabled: !!id,
    });

    const validate = () => {
        if (!state.name) return "O campo \"Nome\" é obrigatório.";
        if (!state.timer) return "O campo \"Tempo máximo para atendimento\" é obrigatório.";
        if (!state.color) return "O campo \"Cor\" é obrigatório.";
        return "";
    };

    const persist = (addAfter = false) => {
        const message = validate();
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        const payload = {
            name: state.name,
            color: state.color,
            priorityDefault: state.priorityDefault,
            timer: state.timer,
            timerInMinutes: timerToMinutes(state.timer),
        };

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? PriorityService.update(id, payload) : PriorityService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/priority/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialPriority);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/priority", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/priority/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir a prioridade "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                PriorityService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/priority", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newPriority = () => navigate(API_DESPACHO_WEB + "/priority/form", false);

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title>Prioridade</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newPriority}><FaPlus/>Adicionar prioridade</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm
                                classNameWrapper="col-span-6"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="name" label="Nome *"
                                value={state.name}
                                onChange={({target: {value}}) => setState(prev => ({...prev, name: value}))}
                            />

                            <InputDatetime
                                classNameWrapper="col-span-4"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                label="Tempo máximo para atendimento (hh:mm) *"
                                returnDateObj={false}
                                onChange={(value) => setState(prev => ({...prev, timer: value ?? ""}))}
                                dateFormat="LT"
                                selected={state.timer}
                            />

                            <div className="col-span-2 flex items-center gap-2">
                                <input
                                    type="color"
                                    className={"h-8 bg-white!"}
                                    value={state.color}
                                    onChange={({target}) => setState(prev => ({...prev, color: target.value}))}
                                />
                            </div>
                            <div className="col-span-12 flex items-center">
                                <InputCheckbox
                                    id="priorityDefault"
                                    label="Padrão" checked={state.priorityDefault}
                                    onChange={({target}) => setState(prev => ({...prev, priorityDefault: target.checked}))}
                                />
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default PriorityForm;